import { useCallback, useRef } from 'react'
import { GET_NEXT_OTC_PACKING } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useInfoToast from 'hooks/toast/useInfoToast'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { usePlusClient } from 'providers/VisionRouter'
import type { Order } from 'types'
import { getLocationIdFromQueryAndContext } from 'utils/location'
import { FulfillmentQueueName } from './useFulfillmentQueue'

let lastFilterSet = {}

type useGetNextOtcOnlyType = {
  startOtcOnlyWork: () => Promise<void>
  getNextOtcOnly: () => Promise<void>
}
const useGetNextOtcOnly = (): useGetNextOtcOnlyType => {
  const {
    client,
    routeTo,
    tokenContext,
    currentLocation: { queryMap },
  } = usePlusClient()
  const showErrorToast = useErrorToast()
  const showInfoToast = useInfoToast()
  const showInfoToastRef = useAutoUpdatingRef(showInfoToast)
  const showErrorToastRef = useAutoUpdatingRef(showErrorToast)
  const streak = useRef(0)

  const getNextOtcOnly = useCallback(async () => {
    try {
      const { locationId, locationIds } = getLocationIdFromQueryAndContext(queryMap, tokenContext)

      const variables = Object.keys(lastFilterSet).length
        ? lastFilterSet
        : {
            selectedLocationIds: queryMap.locationIds || undefined,
            customerIds: queryMap.customerIds || undefined,
            locationId: locationId,
            locationIds: locationIds,
            otcOnly: true,
          }

      const { data } = await client.query({
        query: GET_NEXT_OTC_PACKING,
        fetchPolicy: 'no-cache',
        variables,
      })
      const order = data.getNextOtcPacking as Order

      streak.current += 1

      if (order) {
        showInfoToastRef.current(`Another order is ready for your review. (Streak: ${streak.current})`)
        routeTo
          .order(order._id, true, {
            searchMap: {
              ...lastFilterSet,
              otcOnly: true,
            },
          })
          .now()
      } else {
        const locationId = queryMap.locationId || tokenContext?.locationId
        showInfoToastRef.current('No more work found')
        routeTo
          .fulfillmentQueue(FulfillmentQueueName.Packing, {
            searchMap: {
              locationId,
              locationIds,
              otcOnly: true,
            },
          })
          .now()
      }
    } catch (error) {
      showErrorToastRef.current('Failed to get next OTC Only: ' + error.message)
    }
  }, [queryMap, tokenContext, client, showInfoToastRef, routeTo, showErrorToastRef])

  const startOtcOnlyWork = () => {
    const { locationId, locationIds } = getLocationIdFromQueryAndContext(queryMap, tokenContext)

    lastFilterSet = {
      selectedLocationIds: queryMap.locationIds || undefined,
      medicationNames: queryMap.medications || undefined,
      customerIds: queryMap.customerIds || undefined,
      locationId: locationId,
      locationIds: locationIds,
      otcOnly: true,
    }
    return getNextOtcOnly()
  }

  return { startOtcOnlyWork, getNextOtcOnly }
}

export default useGetNextOtcOnly
